/**
 * partial.ts — Section 3: "Partial Differentiation (Multiple Regression)".
 *
 * A house-price plane: y = 150·size − 2000·age + 50000. Two sliders drive
 * size and age; the predicted price and the two "freeze one variable" 2D
 * partial-derivative plots update live. A 3D Three.js surface shows the plane
 * itself with a marker sphere that tracks the current (size, age, price).
 *
 * Ported from the draft. Differences from the source:
 *   - `THREE` is imported from the npm `three` package instead of the global.
 *   - The mouse-drag handler seeds `previousMousePosition` on mousedown so the
 *     first drag delta doesn't jump from {0,0} (minor UX fix; behaviour is
 *     otherwise identical).
 *   - WebGLRenderer / scene are created eagerly on init; the sphere marker is
 *     repositioned on every slider input.
 *   - Type annotations throughout; no implicit any.
 */

import * as THREE from 'three';

// ---- House-price model: y = 150·size − 2000·age + 50000 ---------------------
// Coefficients are the partial derivatives ∂y/∂x₁ = 150, ∂y/∂x₂ = −2000.
const COEF_SIZE = 150;
const COEF_AGE = -2000;
const INTERCEPT = 50000;

const priceAt = (size: number, age: number): number =>
  COEF_SIZE * size + COEF_AGE * age + INTERCEPT;

// Sliders' domain (mirrors the draft).
const SIZE_MIN = 1000;
const SIZE_MAX = 3000;
const AGE_MIN = 0;
const AGE_MAX = 50;

// ---- 3D mapping -----------------------------------------------------------
// The plane mesh is a unit square in (x, z) ∈ [-5, 5]. We map real (size, age)
// into that square; price is mapped onto the mesh's y axis. These constants
// mirror the draft's deformed-PlaneGeometry construction so the surface and
// the marker sphere line up exactly.
const SIZE_TO_MESH = (size: number): number => (size - 2000) / 200;
const AGE_TO_MESH = (age: number): number => (age - 25) / 5;
const PRICE_TO_MESH_Y = (price: number): number => (price - 300000) / 40000;

export function initPartial(): void {
  const sizeSlider = document.getElementById('sizeSlider') as HTMLInputElement | null;
  const ageSlider = document.getElementById('ageSlider') as HTMLInputElement | null;
  const sizeVal = document.getElementById('sizeVal');
  const ageVal = document.getElementById('ageVal');
  const priceVal = document.getElementById('priceVal');
  const sizeCanvas = document.getElementById('sizeCanvas') as HTMLCanvasElement | null;
  const ageCanvas = document.getElementById('ageCanvas') as HTMLCanvasElement | null;
  const threeCanvas = document.getElementById('threeCanvas') as HTMLCanvasElement | null;
  if (
    !sizeSlider || !ageSlider || !sizeVal || !ageVal || !priceVal ||
    !sizeCanvas || !ageCanvas || !threeCanvas
  ) return;

  const sizeCtx = sizeCanvas.getContext('2d');
  const ageCtx = ageCanvas.getContext('2d');
  if (!sizeCtx || !ageCtx) return;
  // Non-null aliases so drawPartial() gets a CanvasRenderingContext2D, not the
  // union (TS doesn't carry the guard into the nested function).
  const szCtx: CanvasRenderingContext2D = sizeCtx;
  const agCtx: CanvasRenderingContext2D = ageCtx;

  // ---- 2D partial-derivative plots ---------------------------------------

  /**
   * Draws the isolated-variable regression line for either size (isSize=true,
   * age frozen) or age (isSize=false, size frozen), plus the current data
   * point. Mirrors the draft's drawPartialGraph exactly.
   */
  function drawPartial(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    currentX: number,
    fixedX: number,
    minX: number,
    maxX: number,
    isSize: boolean,
  ): void {
    ctx.clearRect(0, 0, width, height);

    const mapGraphX = (x: number): number => ((x - minX) / (maxX - minX)) * (width - 40) + 20;
    // Price domain ~[50k, 500k] → map into the canvas with 20px padding.
    const mapGraphY = (price: number): number =>
      height - 20 - ((price - 50000) / 450000) * (height - 40);

    // Axes.
    ctx.beginPath();
    ctx.strokeStyle = '#e2e8f0';
    ctx.moveTo(20, height - 20);
    ctx.lineTo(width - 20, height - 20);
    ctx.moveTo(20, 20);
    ctx.lineTo(20, height - 20);
    ctx.stroke();

    // Isolated-variable regression line. For "size varied, age frozen": the
    // price formula collapses to 150·size − 2000·age + 50000 — a line in size
    // with slope 150. For "age varied, size frozen": symmetric.
    ctx.beginPath();
    ctx.strokeStyle = isSize ? '#3b82f6' : '#ef4444';
    ctx.lineWidth = 2;
    const startPrice = isSize
      ? COEF_SIZE * minX + COEF_AGE * fixedX + INTERCEPT
      : COEF_SIZE * fixedX + COEF_AGE * minX + INTERCEPT;
    const endPrice = isSize
      ? COEF_SIZE * maxX + COEF_AGE * fixedX + INTERCEPT
      : COEF_SIZE * fixedX + COEF_AGE * maxX + INTERCEPT;
    ctx.moveTo(mapGraphX(minX), mapGraphY(startPrice));
    ctx.lineTo(mapGraphX(maxX), mapGraphY(endPrice));
    ctx.stroke();

    // Current data point.
    const currentPrice = isSize
      ? COEF_SIZE * currentX + COEF_AGE * fixedX + INTERCEPT
      : COEF_SIZE * fixedX + COEF_AGE * currentX + INTERCEPT;
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.arc(mapGraphX(currentX), mapGraphY(currentPrice), 5, 0, Math.PI * 2);
    ctx.fill();
  }

  // ---- 3D surface (Three.js) — progressive enhancement -------------------
  // Built lazily inside a try so a missing/disabled WebGL context (headless
  // browsers, locked-down machines, some VMs) can't take the 2D plots and the
  // slider wiring down with it. setMarker is a no-op if construction failed;
  // the rest of the section keeps working.
  const SIZE_PX = 250;
  const HEIGHT_PX = 200;

  // Default: no-op. Replaced with a real positioner if the 3D scene builds.
  let setMarker = (_size: number, _age: number, _price: number): void => { /* no WebGL */ };

  try {
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf8fafc);
    // Size the renderer to the canvas's CSS box so the surface fills the
    // full-width chart area instead of a 250×200 inset.
    const initW = Math.max(1, threeCanvas.clientWidth) || SIZE_PX;
    const initH = Math.max(1, threeCanvas.clientHeight) || HEIGHT_PX;
    const camera = new THREE.PerspectiveCamera(45, initW / initH, 0.1, 1000);
    // WebGLRenderer throws synchronously when no GL context can be created;
    // that's the boundary we want the try/catch to span.
    const renderer = new THREE.WebGLRenderer({ canvas: threeCanvas, antialias: true });
    renderer.setSize(initW, initH, false);

    const pivot = new THREE.Group();
    // Turn the plane to face the camera (the default orientation had its
    // far edge tilted toward the viewer, i.e. rotated away). This y-rotation
    // swings the +size edge to the front.
    pivot.rotation.y = Math.PI;
    scene.add(pivot);

    // Displace each vertex's y by the price at the (size, age) it represents.
    const geometry = new THREE.PlaneGeometry(10, 10, 10, 10);
    geometry.rotateX(-Math.PI / 2); // lay flat (x, z plane, y up)
    const positions = geometry.attributes.position as THREE.BufferAttribute;
    const arr = positions.array as Float32Array;
    for (let i = 0; i < arr.length; i += 3) {
      const x = arr[i]; // ∈ [-5, 5]
      const z = arr[i + 2]; // ∈ [-5, 5]
      const realSize = x * 200 + 2000;
      const realAge = z * 5 + 25;
      arr[i + 1] = PRICE_TO_MESH_Y(priceAt(realSize, realAge));
    }
    positions.needsUpdate = true;
    geometry.computeVertexNormals();

    const material = new THREE.MeshNormalMaterial({
      side: THREE.DoubleSide,
      opacity: 0.7,
      transparent: true,
    });
    pivot.add(new THREE.Mesh(geometry, material));

    // Wireframe overlay so the surface reads as a grid.
    const wireframe = new THREE.WireframeGeometry(geometry);
    const wire = new THREE.LineSegments(wireframe);
    (wire.material as THREE.LineBasicMaterial).color.setHex(0x000000);
    (wire.material as THREE.LineBasicMaterial).opacity = 0.15;
    (wire.material as THREE.LineBasicMaterial).transparent = true;
    pivot.add(wire);

    // Marker sphere — repositioned on every slider input.
    const sphereGeo = new THREE.SphereGeometry(0.5, 16, 16);
    const sphereMat = new THREE.MeshBasicMaterial({ color: 0x9333ea }); // purple
    const marker = new THREE.Mesh(sphereGeo, sphereMat);
    pivot.add(marker);

    camera.position.set(12, 10, 12);
    camera.lookAt(0, 0, 0);

    // Mouse-drag rotation. Seeds the previous position on mousedown so the
    // first move uses a real delta instead of jumping from {0,0}. The base
    // y-rotation (π) is preserved as the drag origin.
    let dragging = false;
    let prev = { x: 0, y: 0 };
    const onDown = (e: MouseEvent): void => {
      dragging = true;
      prev = { x: e.offsetX, y: e.offsetY };
    };
    const onUp = (): void => { dragging = false; };
    const onMove = (e: MouseEvent): void => {
      if (!dragging) return;
      pivot.rotation.y += (e.offsetX - prev.x) * 0.01;
      pivot.rotation.x += (e.offsetY - prev.y) * 0.01;
      prev = { x: e.offsetX, y: e.offsetY };
    };
    threeCanvas.addEventListener('mousedown', onDown);
    threeCanvas.addEventListener('mouseup', onUp);
    threeCanvas.addEventListener('mouseleave', onUp);
    threeCanvas.addEventListener('mousemove', onMove);

    // Keep the renderer's resolution in sync with the canvas's CSS box as the
    // layout changes (viewport resize, container reflow).
    const resize = (): void => {
      const w = Math.max(1, threeCanvas.clientWidth);
      const h = Math.max(1, threeCanvas.clientHeight);
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', resize);
    // Re-measure on the next frame so the canvas has its final laid-out size.
    requestAnimationFrame(resize);

    const animate3D = (): void => {
      requestAnimationFrame(animate3D);
      renderer.render(scene, camera);
    };
    animate3D();

    setMarker = (size, age, price): void => {
      marker.position.set(SIZE_TO_MESH(size), PRICE_TO_MESH_Y(price), AGE_TO_MESH(age));
    };
  } catch (err) {
    // WebGL unavailable. Leave the 3D canvas blank; the 2D plots and the live
    // price readout below remain fully functional.
    console.warn('[partial] 3D surface disabled —', (err as Error).message);
    threeCanvas.style.opacity = '0.35';
  }

  // ---- combined update ----------------------------------------------------

  function update(): void {
    const size = parseFloat(sizeSlider!.value);
    const age = parseFloat(ageSlider!.value);
    const price = priceAt(size, age);

    sizeVal!.textContent = size.toLocaleString();
    ageVal!.textContent = String(age);
    priceVal!.textContent = price.toLocaleString();

    // Draw at the canvas's own (rounded) box size so the chart fills the
    // canvas instead of rendering as a small 250×200 inset in the corner.
    const sw = Math.max(1, Math.round(sizeCanvas!.clientWidth));
    const sh = Math.max(1, Math.round(sizeCanvas!.clientHeight));
    const aw = Math.max(1, Math.round(ageCanvas!.clientWidth));
    const ah = Math.max(1, Math.round(ageCanvas!.clientHeight));
    if (sizeCanvas!.width !== sw || sizeCanvas!.height !== sh) {
      sizeCanvas!.width = sw;
      sizeCanvas!.height = sh;
    }
    if (ageCanvas!.width !== aw || ageCanvas!.height !== ah) {
      ageCanvas!.width = aw;
      ageCanvas!.height = ah;
    }
    drawPartial(szCtx, sw, sh, size, age, SIZE_MIN, SIZE_MAX, true);
    drawPartial(agCtx, aw, ah, age, size, AGE_MIN, AGE_MAX, false);

    setMarker(size, age, price);
  }

  sizeSlider.addEventListener('input', update);
  ageSlider.addEventListener('input', update);
  update();
}
